import { Outlet, Link, createRootRoute, redirect } from '@tanstack/react-router';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/lib/theme';
import { LocaleProvider, useT } from '@/lib/i18n';
import { initSession, getSessionSync } from '@/lib/auth';
import { getAuthState } from '@/lib/auth/auth-state';
import { useDeepLinkAuth } from '@/hooks/use-deep-link-auth';

function NotFoundComponent() {
  const t = useT();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold">{t('errors.pageNotFound')}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t('errors.pageNotFoundDesc')}</p>
        <div className="mt-6">
          <Link
            to="/today"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t('errors.backHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}

function RootComponent() {
  // 必须在根挂：deep-link 事件可能在 /login 路由还没显示前就到达
  useDeepLinkAuth();
  return (
    <LocaleProvider>
      <ThemeProvider>
        <Outlet />
        <Toaster />
      </ThemeProvider>
    </LocaleProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  beforeLoad: async ({ location }) => {
    // Wait for Supabase to detect any session in URL hash (magic link callback)
    // and read it back from storage before any route renders.
    await initSession();
    const session = getSessionSync();
    const path = location.pathname;

    // Quickbar 是 Tauri 状态栏弹层；它共享同源 localStorage，主窗口已登录时它也能读到 session。
    // 即便没 session 也让 view 自己渲染空态，而不是把 popover 替换成登录页。
    if (path === '/quickbar') return;

    if (path === '/login') {
      if (session) throw redirect({ to: '/today' });
      return; // guest 也可访问 /login（合并选择 / 转化入口）
    }
    // 三态 gate（设计 v4 §4.3）：guest 放行；unset / signedOut 进登录页。
    // getAuthState 防御式（owner 优先于 marker）：marker 残留 + owner≠null 恒判
    // signedOut → 登录墙挡住，无认证读写他人数据的路径不存在。
    if (!session && getAuthState() !== 'guest') {
      throw redirect({ to: '/login' });
    }
    if (path === '/') {
      // authed 与 guest 都落 /today（桌面壳/PWA 冷启动就是 '/'，guest 漏这条会白屏）
      throw redirect({ to: '/today' });
    }
  },
});
